
import {Component, type EventEmitter, h, Prop, State, Event, Listen, Watch, Method} from '@stencil/core';
import type {FilterNode} from '../../result-page/facets-build-wrapper/filter-node';
import {FILTERS_ID_SEPARATOR} from '../../../constants/filterConstants';
import type {FacetChildResponse, FacetResponse} from '../../../models/cose-api/response/facet-options.model';
import {cloneObjectWithNoReference} from '../../../utils/helpers/clone-object-with-no-reference';
import type {FacetChangedEvent} from '../../result-page/search-tab/FacetChangedEvent';
import type { labelProviderInterface } from '../../LabelProviderInterface';

@Component({
  tag: 'single-facet-node',
  styleUrl: './single-facet-node.scss'
})
export class SingleFacetNode {
  @State() toggled = true;
  @Prop() readonly labelProvider?: labelProviderInterface;
  @Prop() readonly filterNode: FilterNode | FacetResponse;
  @Prop() readonly preselectedFilters: string[];
  @Prop() readonly tabName: string;

  @Watch('filterNode')
  onFilterNodeChange(newValue: FilterNode) {
      if(this.preselectedFilters && !newValue.children.find(f => f.selected)) {
        this.localFilterNode = this.selectChildrenByPreselected(cloneObjectWithNoReference(newValue), cloneObjectWithNoReference(this.preselectedFilters));
      } else {
        this.localFilterNode = cloneObjectWithNoReference(newValue);
      }
  }

  @Event({
    eventName: 'changed',
    bubbles: false,
  })
    changedEmitter: EventEmitter<FilterNode>;

  @Event({
    eventName: 'search:FacetClickEvent',
    composed: true,
    cancelable: true,
    bubbles: true
  })
    facetChangedEventEmitter: EventEmitter<FacetChangedEvent>;

  @Listen('search:productCatalogWidgetClicked', { target: 'window' })
  handleProductCatalogWidgetClicked() {
      const widgetAlreadyClicked = window.sessionStorage.getItem('productCatalogWidgetClicked');
      if(widgetAlreadyClicked === null){
        this.productCatalogFacetHiglight = true;
        setTimeout(() => {
            this.productCatalogFacetHiglight = false;
        }, 2000);
      }
  }

  @State() productCatalogFacetHiglight = false;
  @State() localFilterNode: FilterNode | FacetResponse;

  componentWillLoad() {
      if(this.preselectedFilters && !this.filterNode.children.find(f=> f.selected)) {
        this.localFilterNode = this.selectChildrenByPreselected(cloneObjectWithNoReference(this.filterNode), cloneObjectWithNoReference(this.preselectedFilters));
      } else {
        this.localFilterNode = cloneObjectWithNoReference(this.filterNode);
      }
  }

  render() {
      return (
        <div>
          <div class="facet facet-product-catalog" style={this.productCatalogFacetHiglight ? {zIndex: '999'} : null}>
            <a
              class={'facet__header-link'}
              href=""
              onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    this.toggleComponent();
                  }
              }}
            >
              <div
                class={'facet__header'}
                onClick={(e) => {
                    e.preventDefault();
                    this.toggleComponent();
                }}
              >
                <h6 class="facet__title">
                  {this.localFilterNode.id === 'catalog'
                    ? this.labelProvider('product.catalog.catalog', 'Catalog')
                    : this.localFilterNode.displayName
                  }
                </h6>

                <div>
                  <div class={`facet__arrow${this.toggled ? '--rotated' : ''}`}/>
                </div>
              </div>
            </a>
            <div
              class={`facet__body-single-option facet__body-single-option${this.handleOpenView()}`}
            >
              <div
                class={`facet__accordion facet__accordion${this.handleOpenView()}`}
              >
                <se-list option="filter" selectedColor="primary" class="single-option-filter-list">
                  {this.renderList(this.localFilterNode.children as FilterNode[])}
                </se-list>
              </div>
            </div>
          </div>
        </div>
      );
  }

  private selectChildrenByPreselected(facet: FilterNode | FacetResponse, array: string[]): FilterNode | FacetResponse {
      const localFilterNode: FilterNode | FacetResponse = facet;
      let firstPreselectedValue: string;
      const preselectedFiltersArrays = array.map(uniqueId => {
          return uniqueId.split(FILTERS_ID_SEPARATOR);
      });

      preselectedFiltersArrays.forEach(uniqueArray => {
          if(uniqueArray[0] === (localFilterNode as FacetResponse).id) {
            if(!firstPreselectedValue) {
              firstPreselectedValue = uniqueArray[1];
            }
          }
      });

      if(firstPreselectedValue) {
        localFilterNode.children.map(f => {
            if (f.value === firstPreselectedValue) {
              f.selected = true;
            }
        });
      }

      return localFilterNode;
  }

  private emitSingleFilterEventDeselected(deselectedFilterFacetDisplayName: string, deselectedFilter: FacetChildResponse) {
      this.facetChangedEventEmitter.emit({
        event: 'filterClick',
        filterAction: 'Filter deselected',
        filterCategory: deselectedFilterFacetDisplayName,
        filterName: decodeURIComponent(deselectedFilter.displayName),
        filterCount: deselectedFilter.count,
        tabName: this.tabName,
        clickedArea: 'singleFilter'
      });
  }
  private emitSingleFilterEventSelected(newStateDisplayName: string, f: FilterNode) {
      this.facetChangedEventEmitter.emit({
        event: 'filterClick',
        filterAction: 'Filter selected',
        filterCategory: newStateDisplayName,
        filterName: decodeURIComponent(f.displayName),
        filterCount: f.count,
        tabName: this.tabName,
        clickedArea: 'singleFilter'
      });
  }

  private renderList(filteredChildren: FilterNode[]) {
      return (
        <div>
          {filteredChildren.filter(f=> f.count).map((f,i) =>
            <se-list-item
              item={`${f.displayName} (${f.count})`}
              selected={f.selected}
              className="single-option-item-label"
              onClick={() => {
                  const newState = cloneObjectWithNoReference(this.localFilterNode);
                  const deselectedFilterFacet = this.localFilterNode;
                  const deselectedFilter = deselectedFilterFacet.children.find(filter => filter.selected);

                  newState.children.map(filter => {
                      if(filter.selected && f.value !== filter.value) {
                        filter.selected = false;
                      }
                  });
                  newState.children[i].selected = true;
                  this.localFilterNode = newState;

                  if(deselectedFilter && deselectedFilter.value !== f.value) {
                    this.emitSingleFilterEventDeselected(deselectedFilterFacet.displayName, deselectedFilter);
                  }
                  this.emitSingleFilterEventSelected(newState.displayName, f);
                  this.changedEmitter.emit(newState as FilterNode);
              }}
            >
            </se-list-item>
          )}
        </div>
      );
  }

  private handleOpenView() {
      if (this.toggled) {
        return '--open';
      }
      return '--close';
  }

  @Method()
  async toggleComponent(): Promise<void> {
      this.toggled = !this.toggled;
  }
}
